import { useMemo } from "react";
import type { SortConfig } from "@/shared/utils";
import { toggleSort as sharedToggleSort, toNumber } from "@/shared/utils";
import type { InventoryUnit, WarehouseTableRow } from "../types";

export type SortKey = "name" | "status" | "price" | "barcode" | "date";

export type { SortConfig };

export function useWarehouseSort(items: InventoryUnit[], sortConfig: SortConfig<SortKey> | null): InventoryUnit[] {
  return useMemo(() => {
    if (!sortConfig) {
      return items;
    }

    const { key, direction } = sortConfig;

    return [...items].sort((a, b) => {
      let cmp = 0;

      if (key === "name") {
        cmp = a.item_name.localeCompare(b.item_name);
      } else if (key === "status") {
        cmp = a.status.localeCompare(b.status);
      } else if (key === "price") {
        cmp = toNumber(a.unit_cost) - toNumber(b.unit_cost);
      } else if (key === "barcode") {
        cmp = a.barcode.localeCompare(b.barcode);
      } else if (key === "date") {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [items, sortConfig]);
}

function getRowName(row: WarehouseTableRow): string {
  return row.kind === "aggregated" ? row.itemName : row.unit.item_name;
}

function getRowCost(row: WarehouseTableRow): number {
  return row.kind === "aggregated" ? toNumber(row.unitCost) : toNumber(row.unit.unit_cost);
}

function getRowBarcode(row: WarehouseTableRow): string {
  return row.kind === "aggregated" ? "" : row.unit.barcode;
}

function getRowDate(row: WarehouseTableRow): number {
  return row.kind === "aggregated"
    ? new Date(row.units[0].created_at).getTime()
    : new Date(row.unit.created_at).getTime();
}

function getRowStatusKey(row: WarehouseTableRow): string | number {
  return row.kind === "aggregated" ? row.inStockCount : row.unit.status;
}

export function useWarehouseTableSort(rows: WarehouseTableRow[], sortConfig: SortConfig<SortKey> | null): WarehouseTableRow[] {
  return useMemo(() => {
    if (!sortConfig) return rows;

    const { key, direction } = sortConfig;

    return [...rows].sort((a, b) => {
      let cmp = 0;

      if (key === "name") {
        cmp = getRowName(a).localeCompare(getRowName(b));
      } else if (key === "price") {
        cmp = getRowCost(a) - getRowCost(b);
      } else if (key === "barcode") {
        cmp = getRowBarcode(a).localeCompare(getRowBarcode(b));
      } else if (key === "date") {
        cmp = getRowDate(a) - getRowDate(b);
      } else if (key === "status") {
        const sa = getRowStatusKey(a);
        const sb = getRowStatusKey(b);
        if (typeof sa === "number" && typeof sb === "number") {
          cmp = sa - sb;
        } else {
          cmp = String(sa).localeCompare(String(sb));
        }
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [rows, sortConfig]);
}

export function toggleSort(currentSort: SortConfig<SortKey> | null, newKey: SortKey): SortConfig<SortKey> {
  return sharedToggleSort(currentSort, newKey);
}


